import type { Timestamp } from 'firebase/firestore';

export type VerificationStatus =
  | 'canceled'
  | 'pending'
  | 'requires_input'
  | 'unverified'
  | 'verified';

export type VerifiedBadgeStatus = 'unverified' | 'verified';
export type VerificationPaymentStatus =
  | 'failed'
  | 'paid'
  | 'pending'
  | 'refund_pending'
  | 'refunded'
  | 'unpaid';
export type VerifiedAccountWizardStep =
  | 'complete'
  | 'contact'
  | 'identity'
  | 'payment';

export type VerifiedAccount = {
  badge: {
    reasons: string[];
    status: VerifiedBadgeStatus;
    verified: boolean;
    verifiedAt: Timestamp | null;
  };
  email: {
    email: string | null;
    status: 'unverified' | 'verified';
  };
  identity: {
    lastErrorCode: string | null;
    nameLockedAt: Timestamp | null;
    status: VerificationStatus;
    verifiedFullName: string | null;
  };
  payment: {
    amount: number;
    checkoutSessionId: string | null;
    currency: string;
    lastErrorCode: string | null;
    paidAt: Timestamp | null;
    status: VerificationPaymentStatus;
  };
  phone: {
    last4: string | null;
    phoneNumber: string | null;
    status: 'unverified' | 'verified';
  };
};

export function emptyVerifiedAccount(): VerifiedAccount {
  return {
    badge: {
      reasons: [
        'email_unverified',
        'phone_unverified',
        'payment_unpaid',
        'identity_unverified',
      ],
      status: 'unverified',
      verified: false,
      verifiedAt: null,
    },
    email: {
      email: null,
      status: 'unverified',
    },
    identity: {
      lastErrorCode: null,
      nameLockedAt: null,
      status: 'unverified',
      verifiedFullName: null,
    },
    payment: {
      amount: 1000,
      checkoutSessionId: null,
      currency: 'usd',
      lastErrorCode: null,
      paidAt: null,
      status: 'unpaid',
    },
    phone: {
      last4: null,
      phoneNumber: null,
      status: 'unverified',
    },
  };
}

function stringOrNull(value: unknown, maxLength: number) {
  return typeof value === 'string' && value.trim()
    ? value.trim().slice(0, maxLength)
    : null;
}

function timestampOrNull(value: unknown): Timestamp | null {
  return value && typeof value === 'object' && 'toMillis' in value
    ? (value as Timestamp)
    : null;
}

function verificationStatus(value: unknown): VerificationStatus {
  return value === 'verified' ||
    value === 'pending' ||
    value === 'requires_input' ||
    value === 'canceled'
    ? value
    : 'unverified';
}

function verificationPaymentStatus(value: unknown): VerificationPaymentStatus {
  return value === 'paid' ||
    value === 'pending' ||
    value === 'failed' ||
    value === 'refund_pending' ||
    value === 'refunded' ||
    value === 'unpaid'
    ? value
    : 'unpaid';
}

export function parseVerifiedAccount(value: unknown): VerifiedAccount {
  const fallback = emptyVerifiedAccount();

  if (!value || typeof value !== 'object') {
    return fallback;
  }

  const data = value as Record<string, unknown>;
  const badge =
    data.badge && typeof data.badge === 'object'
      ? (data.badge as Record<string, unknown>)
      : {};
  const email =
    data.email && typeof data.email === 'object'
      ? (data.email as Record<string, unknown>)
      : {};
  const identity =
    data.identity && typeof data.identity === 'object'
      ? (data.identity as Record<string, unknown>)
      : {};
  const phone =
    data.phone && typeof data.phone === 'object'
      ? (data.phone as Record<string, unknown>)
      : {};
  const payment =
    data.payment && typeof data.payment === 'object'
      ? (data.payment as Record<string, unknown>)
      : {};
  const badgeVerified = badge.status === 'verified';

  return {
    badge: {
      reasons: Array.isArray(badge.reasons)
        ? badge.reasons.filter(
            (reason): reason is string => typeof reason === 'string',
          )
        : fallback.badge.reasons,
      status: badgeVerified ? 'verified' : 'unverified',
      verified: badgeVerified,
      verifiedAt: timestampOrNull(badge.verifiedAt),
    },
    email: {
      email: stringOrNull(email.email, 254),
      status: email.status === 'verified' ? 'verified' : 'unverified',
    },
    identity: {
      lastErrorCode: stringOrNull(identity.lastErrorCode, 120),
      nameLockedAt: timestampOrNull(identity.nameLockedAt),
      status: verificationStatus(identity.status),
      verifiedFullName: stringOrNull(identity.verifiedFullName, 80),
    },
    payment: {
      amount:
        typeof payment.amount === 'number' && Number.isFinite(payment.amount)
          ? Math.max(0, Math.floor(payment.amount))
          : fallback.payment.amount,
      checkoutSessionId: stringOrNull(payment.checkoutSessionId, 120),
      currency: stringOrNull(payment.currency, 12) ?? fallback.payment.currency,
      lastErrorCode: stringOrNull(payment.lastErrorCode, 120),
      paidAt: timestampOrNull(payment.paidAt),
      status: verificationPaymentStatus(payment.status),
    },
    phone: {
      last4: stringOrNull(phone.last4, 4),
      phoneNumber: stringOrNull(phone.phoneNumber, 20),
      status: phone.status === 'verified' ? 'verified' : 'unverified',
    },
  };
}

export function verifiedAccountIsComplete(
  verification: VerifiedAccount | null | undefined,
) {
  return parseVerifiedAccount(verification).badge.verified === true;
}

export function fullNameIsIdentityLocked(
  verification: VerifiedAccount | null | undefined,
) {
  return Boolean(parseVerifiedAccount(verification).identity.nameLockedAt);
}

export function nextVerifiedAccountWizardStep({
  emailVerified,
  verification,
}: {
  emailVerified: boolean;
  verification: VerifiedAccount | null | undefined;
}): VerifiedAccountWizardStep {
  const normalizedVerification = parseVerifiedAccount(verification);

  if (normalizedVerification.badge.verified) {
    return 'complete';
  }

  if (normalizedVerification.payment.status !== 'paid') {
    return 'payment';
  }

  if (!emailVerified || normalizedVerification.phone.status !== 'verified') {
    return 'contact';
  }

  if (normalizedVerification.identity.status !== 'verified') {
    return 'identity';
  }

  return 'complete';
}
